'use client'

import { cn } from '@/core/lib/utils/cn'
import { usePathname, useRouter } from '@/i18n/navigation'
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
} from '@heroui/react'
import { Globe } from 'lucide-react'
import { Locale, useLocale } from 'next-intl'
import { useParams } from 'next/navigation'
import { useTransition } from 'react'

const localeNames = {
  en: 'English',
  ar: 'العربية',
}

const localeIcons = {
  en: '🇺🇸',
  ar: '🇸🇦',
}

export function LocaleSwitcher() {
  const router = useRouter()
  const [isPending, startTransition] = useTransition()
  const pathname = usePathname()
  const params = useParams()
  const locale = useLocale()

  function changeLocale(nextLocale: Locale) {
    startTransition(() => {
      // @ts-expect-error — validated by next-intl router
      router.replace({ pathname, params }, { locale: nextLocale })
    })
  }


  return (
    <div className='relative inline-block'>
      <Dropdown placement='bottom-end'>
        <DropdownTrigger className='cursor-pointer'>
          <button
            disabled={isPending}
            className={cn(
              'inline-flex items-center gap-2 rounded-xl p-2',
              'text-text',
            )}
          >
            <Globe className='h-5 w-5' />
            <span className='hidden text-sm font-medium md:inline-block'>
              {localeNames[locale]}
            </span>
          </button>
        </DropdownTrigger>

        <DropdownMenu
          aria-label='Select language'
          className='min-w-[140px] p-1'
        >
          {Object.entries(localeNames).map(([code, name]) => (
            <DropdownItem
              key={code}
              onPress={() => changeLocale(code as Locale)}
              className={cn(
                'w-full',
                'flex items-center gap-3',
                'px-3 py-2',
                'rounded-none',
                'transition-colors',
                'hover:bg-default-100 data-[hover=true]:bg-default-100',
              )}
              startContent={
                <span className='text-base'>
                  {localeIcons[code as keyof typeof localeIcons]}
                </span>
              }
              endContent={
                locale === code ? (
                  <div className='bg-primary h-2 w-2 rounded-full' />
                ) : null
              }
            >
              <span className='text-sm font-medium'>{name}</span>
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>

      {isPending && (
        <div className='bg-background/80 absolute inset-0 flex items-center justify-center rounded-xl'>
          <div className='border-primary h-5 w-5 animate-spin rounded-full border-2 border-t-transparent' />
        </div>
      )}
    </div>
  )
}
